import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { post } from "../../api/client";
import type { Session } from "../../api/types";
import { useSession, useSetSession } from "../../auth/session";
import { Button } from "../../components/ui/Button";
import { PasswordField, TextField } from "../../components/ui/Field";
import { InlineError } from "../../components/ui/misc";
import { AuthLayout } from "./AuthLayout";

export function LoginPage() {
  const { data: session } = useSession();
  const setSession = useSetSession();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { from?: string; expired?: boolean } | null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  if (session && !session.mfaPending) return <Navigate to={state?.from ?? "/phone"} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await post<Session>("/auth/login", { email, password });
      setSession(s);
      navigate(s.mfaPending ? "/mfa" : (state?.from ?? "/phone"), { replace: true, state });
    } catch (err) {
      setError(err);
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Sign in" subtitle={state?.expired ? "Your session ended. Please sign in again." : "Use your work email and password."}>
      <form onSubmit={submit} className="space-y-4" noValidate={false}>
        <TextField label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        <PasswordField label="Password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        <InlineError error={error} />
        <Button type="submit" variant="primary" className="w-full" loading={busy}>Sign in</Button>
        <p className="text-center text-sm"><Link className="text-accent-600 hover:underline" to="/forgot-password">Forgot your password?</Link></p>
      </form>
    </AuthLayout>
  );
}
